import { useState, useEffect } from 'react';
import {
  collection,
  collectionGroup,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  setDoc,
  doc,
  getDocs,
  getDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { auth } from '../services/firebase';
import { db, functions } from '../services/firebase';
import type { Document } from '../models/Document';
import { DocumentStatus } from '../models/Document';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';
import { logError, logDebug } from '../../lib/utils';

export function useDocuments(clientId?: string | null, organizationId?: string | null) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);

    let q:
      | ReturnType<typeof query>
      | undefined;
    let needsSorting = false;
    // Documents are stored in subcollections: /projects/{projectId}/documents/{documentId}
    // Use collectionGroup to query across all documents subcollections
    if (clientId) {
      // `clientId` arg is project id in the new model.
      q = query(
        collectionGroup(db, 'documents'),
        where('projectId', '==', clientId)
      );
      needsSorting = true;
    } else if (organizationId && currentUser?.role === UserRole.SUPER_ADMIN) {
      // For super admins with an organizationId, show all documents
      // (they should see all documents regardless of organization)
      q = query(collectionGroup(db, 'documents'));
      needsSorting = true;
    } else if (organizationId) {
      // For regular users, query by top-level client id
      q = query(
        collectionGroup(db, 'documents'),
        where('clientId', '==', organizationId)
      );
      needsSorting = true;
    } else if (!organizationId && currentUser?.role === UserRole.SUPER_ADMIN) {
      // SUPER_ADMIN with no organization filter - see all documents
      // Avoid orderBy to prevent requiring a composite index; we'll sort client-side instead.
      q = query(collectionGroup(db, 'documents'));
      needsSorting = true;
    } else if (!clientId && !organizationId) {
      // Non-super-admin with no context (e.g. public pages) - do not subscribe
      setLoading(false);
      setDocuments([]);
      return;
    }

    if (!q) {
      setLoading(false);
      setDocuments([]);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const documentsData = snapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data() as any;
          const projectId = data.projectId || data.clientId || '';
          const clientIdForUi = projectId;
          const clientAccountId = data.clientId || data.organizationId || '';
          // Handle createdAt at root level or in metadata
          const createdAt = data.createdAt?.toDate() || data.metadata?.createdAt?.toDate() || new Date();
          const updatedAt = data.updatedAt?.toDate() || data.metadata?.updatedAt?.toDate() || new Date();
          return {
            id: docSnapshot.id,
            ...data,
            projectId,
            clientId: clientIdForUi,
            organizationId: clientAccountId,
            createdAt,
            updatedAt,
          } as Document;
        });

        // Sort by createdAt descending (newest first) if we removed orderBy
        if (needsSorting) {
          documentsData.sort((a: Document, b: Document) => {
            const aTime = a.createdAt.getTime();
            const bTime = b.createdAt.getTime();
            return bTime - aTime; // Descending order
          });
        }

        setDocuments(documentsData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        logError('documents', err, { source: 'useDocuments.subscription', clientId, organizationId });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [clientId, organizationId, currentUser?.role]);

  // Helper function to convert Date objects to Timestamps recursively
  // Note: Timestamp objects pass through as-is since they're already valid for Firestore
  const convertDatesToTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    // If it's already a Timestamp, return it as-is
    if (obj instanceof Timestamp) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (Array.isArray(obj)) return obj.map(convertDatesToTimestamps);
    if (typeof obj === 'object' && obj.constructor === Object) {
      // Only process plain objects, not class instances
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          converted[key] = convertDatesToTimestamps(obj[key]);
        }
      }
      return converted;
    }
    return obj;
  };

  const createDocument = async (documentData: Omit<Document, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Documents are stored in subcollections: /projects/{projectId}/documents/{documentId}
      if (!documentData.clientId) {
        throw new Error('clientId is required to create a document');
      }
      if (!documentData.organizationId) {
        throw new Error('organizationId is required to create a document');
      }
      // Convert any Date objects to Timestamps
      const convertedData = convertDatesToTimestamps(documentData);
      // Ensure all required fields are present and properly structured
      const finalData = {
        // New canonical fields
        clientId: documentData.organizationId, // top-level client
        projectId: documentData.clientId,
        // Legacy mirrors
        organizationId: documentData.organizationId,
        clientIdLegacy: documentData.clientId, // previous project reference
        legacyProjectId: documentData.clientId,
        legacyOrganizationId: documentData.organizationId,
        legacyClientId: documentData.clientId,
        templateType: documentData.templateType,
        title: documentData.title,
        description: documentData.description || '',
        status: documentData.status,
        version: documentData.version || 1,
        tags: documentData.tags || [],
        signatures: documentData.signatures || [],
        viewedBy: documentData.viewedBy || [],
        content: documentData.content || '',
        dynamicFields: documentData.dynamicFields || {},
        metadata: convertedData.metadata, // Already converted
      };

      await addDoc(collection(db, `projects/${documentData.clientId}/documents`), finalData);
    } catch (err: any) {
      logError('documents', err, {
        source: 'useDocuments.createDocument',
        clientId: documentData.clientId,
        organizationId: documentData.organizationId,
      });
      if (err?.code) {
        throw err;
      }
      throw new Error(err?.message || 'Failed to create document');
    }
  };

  /**
   * Creates a new document using the markdown shell Cloud Function.
   * This is used for markdown-based documents that are rendered into
   * our standard HTML shell for consistent PDF output.
   */
  const createMarkdownShellDocument = async (params: {
    organizationId: string;
    clientId: string;
    title: string;
    markdown: string;
    logoSource: 'organization' | 'client' | 'both' | 'none';
    dynamicFields?: Record<string, any>;
    description?: string;
    tags?: string[];
  }): Promise<{ documentId: string; clientId: string; organizationId: string }> => {
    try {
      const fn = httpsCallable(functions, 'manageDocument');
      const result = await fn({
        action: 'create',
        ...params,
      });
      return result.data as { documentId: string; clientId: string; organizationId: string };
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.createMarkdownShellDocument' });
      throw err;
    }
  };

  const updateDocument = async (documentId: string, updates: Partial<Document>) => {
    try {
      // Find the document first to get its projectId and construct the correct path
      // This is necessary because documents are in subcollections
      let documentRef;
      let currentClientId: string | undefined;

      // Try to find in current documents list first (more efficient)
      const existingDoc = documents.find(d => d.id === documentId);
      if (existingDoc && (existingDoc as any).projectId) {
        currentClientId = (existingDoc as any).projectId;
        documentRef = doc(db, `projects/${(existingDoc as any).projectId}/documents`, documentId);
      } else if (existingDoc && existingDoc.clientId) {
        // Legacy fallback
        currentClientId = existingDoc.clientId;
        documentRef = doc(db, `projects/${existingDoc.clientId}/documents`, documentId);
      } else {
        // If not in list, query to find it
        const q = organizationId
          ? query(collectionGroup(db, 'documents'), where('clientId', '==', organizationId))
          : query(collectionGroup(db, 'documents'));
        const snapshot = await getDocs(q);
        const foundDoc = snapshot.docs.find(d => d.id === documentId);

        if (!foundDoc) {
          throw new Error('Document not found');
        }

        currentClientId = foundDoc.data().projectId || foundDoc.data().clientId;
        // Use the document reference from the query result
        documentRef = foundDoc.ref;
      }

      // Check if project (legacy clientId field) is being changed
      const newClientId = updates.clientId;
      const isClientIdChanging = newClientId && newClientId !== currentClientId;

      if (isClientIdChanging) {
        // Need to move the document to a new subcollection
        // First, get the full document data
        const docSnap = await getDoc(documentRef);
        if (!docSnap.exists()) {
          throw new Error('Document not found');
        }

        const currentData = docSnap.data();
        
        // Create the document in the new location with all updates
        const convertedUpdates = convertDatesToTimestamps(updates);
        const newDocumentData = {
          ...currentData,
          ...convertedUpdates,
          'metadata.updatedAt': Timestamp.now(),
          'metadata.lastModifiedBy': currentUser?.uid || '',
        };

        // Remove id and metadata fields that shouldn't be copied
        delete newDocumentData.id;
        // Keep metadata but update it
        if (newDocumentData.metadata) {
          newDocumentData.metadata = {
            ...newDocumentData.metadata,
            lastModifiedBy: currentUser?.uid || newDocumentData.metadata.lastModifiedBy,
            updatedAt: Timestamp.now(),
          };
        }

        // Create in new location with the same document ID
        const newDocumentRef = doc(db, `projects/${newClientId}/documents`, documentId);
        await setDoc(newDocumentRef, newDocumentData);

        // Delete from old location
        await deleteDoc(documentRef);
      } else {
        // Normal update - clientId not changing
        await updateDoc(documentRef, {
          ...updates,
          'metadata.updatedAt': Timestamp.now(),
          'metadata.lastModifiedBy': currentUser?.uid || '',
        });
      }
    } catch (err) {
      logError('documents', err, { source: 'useDocuments.updateDocument', documentId });
      throw err;
    }
  };

  const deleteDocument = async (documentId: string, clientId?: string) => {
    try {
      // Ensure user is authenticated before calling the function
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated to delete documents');
      }
      
      // Wait for auth token to be ready
      const idToken = await user.getIdToken();
      logDebug('documents', 'Calling manageDocument delete', { 
        documentId, 
        clientId,
        userId: user.uid,
        isAuthenticated: !!user,
        hasToken: !!idToken
      });
      
      // Create the callable function - it should automatically include the auth token
      const manageDocumentFn = httpsCallable(functions, 'manageDocument');
      const result = await manageDocumentFn({
        action: 'delete',
        documentId,
        clientId,
      });
      logDebug('documents', 'manageDocument delete result', result);
    } catch (err: any) {
      // Enhanced error logging for Firebase callable functions
      const errorDetails = {
        source: 'useDocuments.deleteDocument',
        documentId,
        clientId,
        errorCode: err?.code,
        errorMessage: err?.message,
        errorDetails: err?.details,
        fullError: err,
      };
      logError('documents', err, errorDetails);
      
      // Re-throw with a more user-friendly message if possible
      if (err?.code === 'functions/not-found') {
        throw new Error('The delete function is not available. Please check if it is deployed.');
      } else if (err?.code === 'functions/permission-denied' || err?.code === 'permission-denied') {
        throw new Error('You do not have permission to delete this document.');
      } else if (err?.code === 'functions/unauthenticated' || err?.code === 'unauthenticated') {
        throw new Error('You must be logged in to delete documents. Please sign in and try again.');
      } else if (err?.code === 'functions/unavailable' || err?.code === 'unavailable') {
        throw new Error('The service is temporarily unavailable. Please try again later.');
      } else if (err?.message) {
        // Check if the error message indicates authentication issues
        if (err.message.includes('not authenticated') || err.message.includes('Authorization header')) {
          throw new Error('Authentication failed. Please sign out and sign in again, then try deleting the document.');
        }
        throw new Error(err.message);
      } else {
        throw new Error('Failed to delete document. Please try again.');
      }
    }
  };

  /**
   * Duplicates a document, creating a new copy with all fields except signatures.
   * The new document will have status DRAFT, version 1, and "(Copy)" appended to the title.
   */
  const duplicateDocument = async (documentId: string) => {
    try {
      if (!currentUser) {
        throw new Error('User must be authenticated to duplicate a document');
      }

      // Find the document first to get its data
      let sourceDoc: Document | null = null;

      // Try to find in current documents list first (more efficient)
      const existingDoc = documents.find(d => d.id === documentId);
      if (existingDoc) {
        sourceDoc = existingDoc;
      } else {
        // If not in list, query to find it
        const q = organizationId
          ? query(collectionGroup(db, 'documents'), where('clientId', '==', organizationId))
          : query(collectionGroup(db, 'documents'));
        const snapshot = await getDocs(q);
        const foundDoc = snapshot.docs.find(d => d.id === documentId);

        if (!foundDoc) {
          throw new Error('Document not found');
        }

        const data = foundDoc.data() as any;
        const projectId = data.projectId || data.clientId || '';
        const clientAccountId = data.clientId || data.organizationId || '';
        const createdAt = data.createdAt?.toDate() || data.metadata?.createdAt?.toDate() || new Date();
        const updatedAt = data.updatedAt?.toDate() || data.metadata?.updatedAt?.toDate() || new Date();
        sourceDoc = {
          id: foundDoc.id,
          ...data,
          projectId,
          clientId: projectId,
          organizationId: clientAccountId,
          createdAt,
          updatedAt,
        } as Document;
      }

      if (!sourceDoc) {
        throw new Error('Document not found');
      }

      // Create duplicate with all fields except signatures and signing-related fields
      const now = new Date();
      const duplicateData: Omit<Document, 'id' | 'createdAt' | 'updatedAt'> = {
        organizationId: sourceDoc.organizationId,
        clientId: sourceDoc.clientId,
        templateType: sourceDoc.templateType,
        title: `${sourceDoc.title} (Copy)`,
        description: sourceDoc.description,
        status: DocumentStatus.DRAFT,
        version: 1,
        tags: [...(sourceDoc.tags || [])],
        signatures: [], // Don't duplicate signatures
        content: sourceDoc.content,
        dynamicFields: { ...(sourceDoc.dynamicFields || {}) },
        metadata: {
          createdBy: currentUser.uid,
          lastModifiedBy: currentUser.uid,
          createdAt: now,
          updatedAt: now,
          version: 1,
        },
        viewedBy: [], // Don't duplicate view history
        // Don't duplicate signing tokens or PDF URLs
        // signingToken, signingTokenExpiresAt, signingTokenUsed, signedPdfUrl, pdfUrl are omitted
        logoSource: sourceDoc.logoSource,
        orgLogoUrl: sourceDoc.orgLogoUrl,
        clientLogoUrl: sourceDoc.clientLogoUrl,
      };

      await createDocument(duplicateData);
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.duplicateDocument', documentId });
      throw err;
    }
  };

  /**
   * Sends a signing link to a client for a document
   */
  const sendSigningLink = async (documentId: string, clientEmail: string, sendEmail: boolean = true): Promise<{ signingLink: string; token: string; expiresAt: string }> => {
    try {
      // Get the current user's ID token for authentication
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      const idToken = await user.getIdToken();

      // Call the HTTP function with proper CORS headers
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/sendDocumentSigningLink`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ documentId, clientEmail, sendEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result as { signingLink: string; token: string; expiresAt: string };
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.sendSigningLink', documentId });
      throw err;
    }
  };

  /**
   * Validates a document signing token (public endpoint)
   */
  const validateDocumentToken = async (token: string): Promise<any> => {
    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/validateDocumentSigningToken?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          // Don't include Content-Type for GET requests to avoid preflight
        }
      );

      // Check content type to ensure it's JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate token');
      }

      return data;
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.validateDocumentToken' });
      if (err instanceof Error) {
        throw err;
      }
      throw new Error(err.message || 'Failed to validate token. Please try again.');
    }
  };

  /**
   * Submits a client signature for a document (public endpoint)
   */
  const submitDocumentSignature = async (
    token: string,
    signatureType: 'draw' | 'type',
    signatureImage?: string,
    typedName?: string,
    signingDate?: string,
    consentGiven: boolean = true
  ): Promise<{ success: boolean; message: string; signedPdfUrl?: string | null }> => {
    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/submitDocumentSignature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            signatureType,
            signatureImage,
            typedName,
            signingDate,
            consentGiven,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to submit signature (${response.status} ${response.statusText})`;
        const contentType = response.headers.get('content-type');

        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
            logError('documents', new Error(errorMessage), {
              source: 'useDocuments.submitDocumentSignature.serverError',
              status: response.status,
            });
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
            logError('documents', new Error(errorMessage), {
              source: 'useDocuments.submitDocumentSignature.serverTextError',
              status: response.status,
            });
          }
        } catch (parseError: any) {
          logError('documents', parseError, {
            source: 'useDocuments.submitDocumentSignature.parseError',
            status: response.status,
          });
          errorMessage = response.statusText || errorMessage;
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        throw error;
      }

      const result = await response.json();
      return result;
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.submitDocumentSignature' });
      throw err;
    }
  };

  /**
   * Submits an organization signature for a document (authenticated)
   * Uses unified submitDocumentSignature function with auth token
   */
  const submitOrganizationDocumentSignature = async (
    documentId: string,
    signatureType: 'draw' | 'type',
    signatureImage?: string,
    typedName?: string,
    signingDate?: string
  ): Promise<{ success: boolean; message: string; signedPdfUrl?: string | null }> => {
    setLoading(true);
    setError(null);

    try {
      logDebug('documents', 'submitOrganizationDocumentSignature.call', {
        documentId,
        signatureType,
        hasSignatureImage: !!signatureImage,
        hasTypedName: !!typedName,
        signingDate,
      });

      // Get auth token for authenticated request
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      const idToken = await user.getIdToken();

      // Use unified submitDocumentSignature function with auth header
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/submitDocumentSignature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            documentId,
            signatureType,
            signatureImage,
            typedName,
            signingDate,
            consentGiven: true, // Org signatures always have consent
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit signature');
      }

      const result = await response.json();
      logDebug('documents', 'submitOrganizationDocumentSignature.result', result);

      return {
        success: result.success || true,
        message: result.message || 'Signature submitted successfully',
        signedPdfUrl: result.signedPdfUrl || null,
      };
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.submitOrganizationDocumentSignature', documentId });
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generates a PDF from document HTML content on demand
   */
  const generateDocumentPdf = async (documentId: string): Promise<{ success: boolean; pdfUrl: string }> => {
    try {
      const fn = httpsCallable(functions, 'generateDocumentPdf');
      const result = await fn({ documentId });
      return result.data as { success: boolean; pdfUrl: string };
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.generateDocumentPdf', documentId });
      throw err;
    }
  };

  /**
   * Enhances a document's PDF with logo, footer, and improved formatting for presentation
   */
  const enhanceDocumentPdf = async (
    documentId: string,
    options?: {
      logoSource?: 'organization' | 'client' | 'both' | 'none';
      orgLogoUrl?: string;
      clientLogoUrl?: string;
      footerText?: string;
    }
  ): Promise<{ success: boolean; pdfUrl: string }> => {
    try {
      const fn = httpsCallable(functions, 'enhanceDocumentPdf');
      const result = await fn({ documentId, options });
      return result.data as { success: boolean; pdfUrl: string };
    } catch (err: any) {
      logError('documents', err, { source: 'useDocuments.enhanceDocumentPdf', documentId });
      throw err;
    }
  };

  return {
    documents,
    loading,
    error,
    createDocument,
    createMarkdownShellDocument,
    updateDocument,
    deleteDocument,
    duplicateDocument,
    sendSigningLink,
    validateDocumentToken,
    submitDocumentSignature,
    submitOrganizationDocumentSignature,
    generateDocumentPdf,
    enhanceDocumentPdf,
  };
}
